import cn from "classnames";
import { Field, Select } from "components/shared/form";
import { requiredField } from "helpers/validators";
import Tooltip from "rc-tooltip";
import React, { useEffect, useRef, useState } from "react";
import { getBanks, updateFinancial } from "sources/profile/organization";

const emptyValue = (value) => !String(value || "").trim();
const getFeatureText = (feature, periodInfo) =>
    feature.dynamic
        ? `${periodInfo.totalMonths * 30} дней с момента оплаты до ${periodInfo.vehicleLimit} ТС`
        : feature.text;

const bankSelectStyles = {
    control: (styles, { isFocused }) => ({
        ...styles,
        minHeight: "44px",
        border: "none",
        borderRadius: "8px",
        boxShadow: isFocused ? "0 0 0 1px #ffb800" : "none",
    }),
    valueContainer: (styles) => ({ ...styles, minHeight: "44px", padding: "0 14px" }),
    indicatorsContainer: (styles) => ({ ...styles, height: "44px" }),
    input: (styles) => ({ ...styles, height: "24px", margin: 0, padding: 0 }),
    menuPortal: (styles) => ({ ...styles, zIndex: 10000 }),
};

export default function TariffSelectionFinancialForm({
    loading = false,
    organization,
    period,
    periodInfo,
    features = [],
    onContinue,
    onDefer,
}) {
    const rootRef = useRef(null);
    const [formData, setFormData] = useState({
        bankId: organization?.bankId || "",
        bank: organization?.bank || "",
        ks: organization?.ks || "",
        rs: organization?.rs || "",
    });
    const [manualInput, setManualInput] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const modalWindow = rootRef.current?.closest(".modal-window");
        modalWindow?.classList.add("modal-window--tariff-financial");

        return () => {
            modalWindow?.classList.remove("modal-window--tariff-financial");
        };
    }, []);

    const setValue = ({ name, value }) => {
        setInvalid(false);
        if (name === "bankId" && !manualInput) {
            setFormData((prev) => ({ ...prev, bank: "", bankId: value, ks: "" }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const isValid = () => ["bankId", "bank", "ks", "rs"].every((name) => !emptyValue(formData[name]));
    const hasBankData = manualInput || !emptyValue(formData.bank) || !emptyValue(formData.ks);

    const handleManualInput = () => {
        setInvalid(false);
        setManualInput(true);
    };

    const handleBankChange = ({ value, option }) => {
        setInvalid(false);
        setFormData((prev) =>
            option
                ? {
                      ...prev,
                      bank: option.bank || "",
                      bankId: option.bankId || value || "",
                      ks: option.ks || "",
                      rs: option.rs || prev.rs,
                  }
                : {
                      ...prev,
                      bank: "",
                      bankId: value || "",
                      ks: "",
                  },
        );
    };

    const handleSave = () => {
        if (!isValid()) {
            setInvalid(true);
            if (!hasBankData) {
                setManualInput(true);
            }
            return;
        }

        setSaving(true);
        updateFinancial({
            id: organization?.id,
            inn: organization?.inn,
            kpp: organization?.kpp,
            ogrn: organization?.ogrn,
            bankId: formData.bankId,
            bank: formData.bank,
            ks: formData.ks,
            rs: formData.rs,
        })
            .then((res) => {
                if (res) {
                    onContinue?.();
                } else {
                    setInvalid(true);
                }
                return null;
            })
            .catch(() => setInvalid(true))
            .finally(() => setSaving(false));
    };

    const renderField = (label, name, placeholder, options = {}) => {
        const isRequired = options.required !== false;

        return (
            <Field
                labelWrapper="tariff-financial-form__label"
                fieldWrapper="tariff-financial-form__field"
                bWrapper={cn("tariff-financial-form__field-wrap", options.className)}
                label={label}
                inputProps={{ name, placeholder }}
                value={formData[name]}
                onChange={setValue}
                required={isRequired}
                validators={isRequired ? [requiredField] : []}
            />
        );
    };

    const renderBankSelect = () => (
        <Select
            labelWrapper="tariff-financial-form__label"
            fieldWrapper="tariff-financial-form__field tariff-financial-form__field--select"
            bWrapper="tariff-financial-form__field-wrap tariff-financial-form__field-wrap--select"
            label="БИК"
            inputProps={{ name: "bankId" }}
            placeholder="Например: 044525225"
            value={formData.bankId}
            onChange={handleBankChange}
            required
            validators={[requiredField]}
            url={getBanks}
            propName={(item) => [item.bankId, item.bank].filter(Boolean).join(" - ")}
            idKey="bankId"
            minimumInputLength={1}
            menuPortalTarget={document.body}
            styles={bankSelectStyles}
        />
    );

    const renderFeaturesTooltip = () => (
        <div className="tariff-summary-card__features-tooltip">
            {features.map((feature, idx) => (
                <div key={idx} className="tariff-summary-card__feature">
                    <i className={feature.icon} />
                    <span>{getFeatureText(feature, periodInfo)}</span>
                </div>
            ))}
        </div>
    );

    return (
        <div
            ref={rootRef}
            className={cn("tariff-flow-modal tariff-financial-form", { "loading loading--small": saving || loading })}
        >
            <div className="tariff-flow-modal__header">
                <h2>Получите счет и начните работать уже сегодня</h2>
                <p>Это займет меньше одной минуты, остальное заполним автоматически</p>
            </div>

            <div className="tariff-flow-modal__body">
                <div className="tariff-summary-card">
                    <div className="tariff-summary-card__title">
                        <i className="icon-uEA60-calendar" />
                        Тариф {periodInfo.title}
                    </div>
                    <div className="tariff-summary-card__subtitle">Ежемесячная подписка</div>
                    <div className="tariff-summary-card__price">{periodInfo.pricePerMonth.toLocaleString()} ₽/мес.</div>
                    <div className="tariff-summary-card__total">
                        {period.price.toLocaleString()} ₽ за {periodInfo.title}
                    </div>
                    <div className="tariff-summary-card__limit">до {periodInfo.vehicleLimit} ТС</div>
                    <Tooltip overlay={renderFeaturesTooltip()} placement="top">
                        <button className="tariff-summary-card__link" type="button">
                            <i className="icon-uEA88-info-circle" />
                            Что входит в тариф
                        </button>
                    </Tooltip>
                </div>

                <div className="tariff-financial-form__panel">
                    {manualInput && (
                        <div className="tariff-financial-form__manual-title">
                            <span />
                            РУЧНОЙ ВВОД
                            <span />
                        </div>
                    )}

                    <div className="tariff-financial-form__bank-id">
                        {manualInput
                            ? renderField("БИК", "bankId", "Например: 044525225", { className: "mb-2" })
                            : renderBankSelect()}
                    </div>

                    {!hasBankData && (
                        <div className="tariff-financial-form__hint">
                            Выберите банк из списка, чтобы автоматически заполнить банк и корр. счет. Если банка нет,{" "}
                            <button type="button" onClick={handleManualInput}>
                                введите данные вручную
                            </button>
                        </div>
                    )}

                    {hasBankData && (
                        <>
                            {renderField("Наименование банка", "bank", "Например: ПАО Сбербанк")}
                            {renderField("Корреспондентский счет", "ks", "")}
                        </>
                    )}

                    {hasBankData && renderField("Расчетный счет", "rs", "Например: 40702810400000000000")}

                    {invalid && (
                        <div className="tariff-financial-form__error">
                            Заполните банковские реквизиты или проверьте введенные данные
                        </div>
                    )}

                    <div className="tariff-financial-form__payment-note">
                        <span className="tariff-financial-form__shield-icon" />
                        <div>
                            <div>Оплата по счету - без автосписания</div>
                            <small>Вы получите счет в личном кабинете и оплатите когда удобно</small>
                        </div>
                    </div>

                    <button
                        className="tariff-financial-form__submit"
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        Получить счет <span>&rarr;</span>
                    </button>

                    <div className="tariff-financial-form__access-note">
                        После оплаты вы сразу получите доступ к системе
                    </div>
                </div>
            </div>

            <button
                className="tariff-financial-form__defer"
                type="button"
                onClick={onDefer}
                disabled={saving || loading}
            >
                Позже <span>(Счет сохранится)</span>
            </button>
        </div>
    );
}
